import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { ModuleGuide } from "./ModuleGuide";
import { StepBar, Primary, Footer, Num, PropertySelect, Stat, Disclaimer, eur } from "./ui";

const LABELS = ["Données d'achat", "Estimation", "Validation"];

const PurchaseStep = ({ properties, pid, setPid, f, set }) => {
  const p = properties.find((x) => x.id === pid) || {};
  return (
    <div className="space-y-4" data-testid="val-purchase">
      <PropertySelect properties={properties} value={pid} onChange={setPid} testid="val-property-select" />
      <div className="grid sm:grid-cols-2 gap-4">
        {!p.purchase_price && <Num label="Prix d'acquisition" suffix="€" value={f.purchase_price} onChange={(v) => set("purchase_price", v)} testid="val-price" />}
        {!p.purchase_date && <label className="block"><span className="block text-xs font-medium text-brand-green/70 mb-1">Date d'achat</span><input type="date" className="input" value={f.purchase_date} onChange={(e) => set("purchase_date", e.target.value)} data-testid="val-date" /></label>}
        {!p.acquisition_fees && <Num label="Frais de notaire" suffix="€" optional value={f.acquisition_fees} onChange={(v) => set("acquisition_fees", v)} testid="val-fees" />}
        {!p.works_amount && <Num label="Travaux réalisés" suffix="€" optional value={f.works_amount} onChange={(v) => set("works_amount", v)} testid="val-works" />}
        {!p.surface && <Num label="Surface" suffix="m²" value={f.surface} onChange={(v) => set("surface", v)} testid="val-surface" />}
      </div>
      {p.purchase_price && p.purchase_date && p.surface ? <p className="text-sm text-brand-green/60" data-testid="val-purchase-complete">Les données d'achat de ce bien sont déjà renseignées.</p> : null}
    </div>
  );
};

const Success = ({ r, onDone }) => {
  const nav = useNavigate();
  return (
    <div className="pb-2" data-testid="val-success">
      <CheckCircle2 className="w-10 h-10 text-brand-limeDark" />
      <h3 className="font-display text-2xl text-brand-greenDeep mt-3">Votre bien vaut environ {eur(r.value)}</h3>
      {r.low && r.high && r.low !== r.high && <p className="text-sm text-brand-green/60">Fourchette {eur(r.low)} – {eur(r.high)} · source {r.source} · {r.date}</p>}
      <div className="grid sm:grid-cols-2 gap-3 mt-5">
        {r.latent_gain != null && <Stat label="Plus-value latente" value={eur(r.latent_gain)} tone={r.latent_gain < 0 ? "text-red-600" : ""} testid="val-gain" />}
        <Stat label="Patrimoine net consolidé" value={r.has_loan ? eur(r.net_worth) : eur(r.total_value)} testid="val-networth" />
        {r.gross_yield != null && <Stat label="Rendement brut (valeur actuelle)" value={`${r.gross_yield} %`} testid="val-gross" />}
        {r.net_yield != null && <Stat label="Rendement net de charges" value={`${r.net_yield} %`} testid="val-net" />}
      </div>
      {!r.has_loan && <p className="text-xs text-amber-800 mt-3">Patrimoine net incomplet : <button onClick={() => { onDone(); nav("/app/finances/prets?guide=loans"); }} className="underline font-semibold" data-testid="val-add-loan">Ajouter mon prêt</button></p>}
      <Disclaimer>Estimation indicative fondée sur les ventes comparables (DVF), réestimée chaque trimestre. Ne remplace pas une expertise professionnelle. Une valeur saisie par vous n'est jamais écrasée.</Disclaimer>
      <Footer><Primary onClick={onDone} data-testid="val-done">Terminer</Primary></Footer>
    </div>
  );
};

const Steps = ({ properties, onDone }) => {
  const [step, setStep] = useState(1);
  const [pid, setPid] = useState(properties[0]?.id);
  const [f, setF] = useState({ purchase_price: "", purchase_date: "", acquisition_fees: "", works_amount: "", surface: "" });
  const [est, setEst] = useState(null);
  const [manual, setManual] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF({ ...f, [k]: v });
  const estimate = async () => {
    setBusy(true);
    try { setEst((await api.get(`/guides/valuation/${pid}`, { params: { surface: Number(f.surface) || 0 } })).data); setStep(2); } finally { setBusy(false); }
  };
  const save = async (mode) => {
    setBusy(true);
    const body = { mode, value: Number(manual) || null };
    Object.entries(f).forEach(([k, v]) => { if (v !== "") body[k] = k === "purchase_date" ? v : Number(v); });
    try { setRes((await api.post(`/guides/valuation/${pid}`, body)).data); setStep(4); } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); } finally { setBusy(false); }
  };
  if (step === 4 && res) return <Success r={res} onDone={onDone} />;
  return (
    <div data-testid="val-steps">
      <StepBar step={Math.min(step, 3)} total={3} labels={LABELS} />
      {step === 1 && <PurchaseStep properties={properties} pid={pid} setPid={setPid} f={f} set={set} />}
      {step === 2 && est && (est.estimate ? (
        <div data-testid="val-estimate">
          <p className="text-sm text-brand-green/70">D'après {est.estimate.comps} vente(s) comparable(s) autour de votre bien :</p>
          <p className="font-display text-3xl text-brand-greenDeep mt-2" data-testid="val-estimate-value">{eur(est.estimate.low)} – {eur(est.estimate.high)}</p>
          <p className="text-xs text-brand-green/60">Valeur centrale {eur(est.estimate.value)} · {eur(est.estimate.price_per_m2)}/m² · {est.estimate.source} · {est.estimate.date}</p>
        </div>
      ) : <p className="text-sm text-amber-800" data-testid="val-no-estimate">{est.message || "Pas d'estimation automatique disponible."} Saisissez votre propre valeur à l'étape suivante.</p>)}
      {step === 3 && (
        <div className="space-y-3" data-testid="val-validate">
          {est?.estimate && <button onClick={() => save("dvf")} className="w-full rounded-2xl border border-brand-limeDark bg-brand-lime/10 p-4 text-left" data-testid="val-accept"><b>J'accepte l'estimation</b> · {eur(est.estimate.value)}</button>}
          <div className="rounded-2xl border border-brand-soft p-4"><Num label="Ou je saisis ma propre valeur" suffix="€" value={manual} onChange={setManual} testid="val-manual" />
            <button onClick={() => save("manual")} disabled={!manual || busy} className="mt-3 px-4 py-2 rounded-full bg-brand-greenDeep text-white text-sm font-semibold disabled:opacity-40" data-testid="val-manual-save">Utiliser ma valeur</button></div>
        </div>
      )}
      <Footer onBack={step > 1 ? () => setStep(step - 1) : null}>
        {step === 1 && <Primary busy={busy} onClick={estimate} data-testid="val-next-1">Estimer mon bien</Primary>}
        {step === 2 && <Primary onClick={() => setStep(3)} data-testid="val-next-2">Continuer</Primary>}
      </Footer>
    </div>
  );
};

const Preview = () => (
  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-center"><Stat label="Valeur estimée" value="245 000 €" /><Stat label="Plus-value latente" value="+38 000 €" /><Stat label="Patrimoine net" value="96 400 €" /></div>
);

export const ValuationGuide = () => (
  <ModuleGuide module="valuation" label="Guide Valorisation" autoOpen={false} intro="Combien valent vos biens aujourd'hui ?"
    benefit="Une fourchette de valeur issue des ventes réelles autour de chez vous, votre plus-value latente et votre patrimoine net." preview={<Preview />} Steps={Steps} />
);
